import {
  useGenerateAwaitingInvoiceCSVData,
  useGenerateMentorDataCSVData,
  useSendAwaitingInvoiceReminders,
  useGetExchangeRateByMonth,
  useGetNoOfActiveMentorsByMonth,
  useGetNoOfSessionsByMonth,
  useGetReportsFinancialStats,
  useGetTotalPaymentByMonth,
  useMarkBulkReportsAsPaid,
  useMarkBulkReportsReadyForPayment,
  useGetTotalPaymentForExtraServiceByMonth,
  useSearchReports,
} from '@actions';
import { SearchOutlined } from '@ant-design/icons';
import { ElectronicInvoiceModal } from '@components';
import SideBySideModal from '@components/SideBySideModal';
import Pagination from '@components/V4/Pagination';
import V4Table from '@components/V4/Table/Table';
import apiClient from '@contexts/AuthContext/apiClient';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DotsVerticalIcon,
  DownloadIcon,
  ExclamationIcon,
  MailIcon,
  PlusIcon,
} from '@heroicons/react/outline';
import { useDebounce } from '@hooks';
import ActivityReportTable from '@modules/Admin/ActivityReport/ActivityReportTable';
import ConfirmRemindersModal from '@modules/Admin/ActivityReport/ConfirmRemindersModal';
import GenerateReportModal from '@modules/Admin/ActivityReport/GenerateReportModal';
import MentorActivityTab from '@modules/Admin/ActivityReport/MentorActivityTab';
import StuckReportsModal from '@modules/Admin/ActivityReport/StuckReportsModal';
import { reportApis } from '@shared/apis';
import { Report } from '@shared/common';
import { ReportStatus } from '@shared/enums';
import styles from '@styles/components/calendar.module.scss';
import dropdown from '@styles/components/reusables/dropdown.module.scss';
import reportsStyles from '@styles/pages/admin/activity-report.module.scss';
import { Button, Checkbox, Dropdown, Input, Modal, Select } from 'antd';
import useToast from 'apps/agora/src/hooks/useToast';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import { stringify } from 'csv-stringify/sync';
import JSZip from 'jszip';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useCalendar from '../../../hooks/useCalendar';
import { logger } from '../../../utils/logger';
import InformationCard from '../../Mentor/SessionHistory/InformationCard';
import ExchangeRatesTab from './ExchangeRatesTab';

interface StatInfo {
  title: string;
  description: string;
  withMentorBreakdown?: boolean;
}

interface StatCardProps extends StatInfo {
  value?: string;
  cardSubtitle: string;
  onInfoClick: (info: StatInfo) => void;
}

const StatCard = (props: StatCardProps) => {
  const { value, cardSubtitle, title, description, withMentorBreakdown, onInfoClick } = props;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '260px',
      }}
    >
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <InformationCard title={value} subtitle={cardSubtitle} width="260px" height="136px" />
        <button
          type="button"
          onClick={() => onInfoClick({ title, description, withMentorBreakdown })}
          aria-label={`What does "${title}" mean?`}
          title={description}
          style={{
            position: 'absolute',
            top: '8px',
            right: '12px',
            width: '24px',
            height: '24px',
            borderRadius: '50%',
            border: 'none',
            background: '#FFFFFF',
            color: '#33A9AB',
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          ?
        </button>
      </div>
      <h4 style={{ margin: '8px 0 0', textAlign: 'center' }}>{title}</h4>
    </div>
  );
};

interface CurrencySelectorProps {
  currencies: string[];
  goLeft: any;
  goRight: any;
  selectedCurrency: number;
}

const CurrencySelector = (props: CurrencySelectorProps) => {
  return (
    <div className={reportsStyles.currencySelectorContainer}>
      <ChevronLeftIcon
        className={`${styles.icon} ${reportsStyles.directionIcon}`}
        onClick={props.goLeft}
      />
      {props.currencies[props.selectedCurrency]}
      <ChevronRightIcon
        className={`${styles.icon} ${reportsStyles.directionIcon}`}
        onClick={props.goRight}
      />
    </div>
  );
};

const suffix = <SearchOutlined />;

const TARGET_DATE_FORMAT = 'YYYY-MM';

const ActivityReport = () => {
  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);
  const enrollmentId = searchParams.get('enrollmentId');
  const sessionId = searchParams.get('sessionId');
  const selectedDateParams = searchParams.get('selectedDate');

  const toast = useToast();

  const defaultDate = moment().subtract(1, 'months').format(TARGET_DATE_FORMAT);

  if (!selectedDateParams) {
    searchParams.set('selectedDate', defaultDate);

    history.replace(`${location.pathname}?${searchParams.toString()}`);
  }

  const [selectedDate, setSelectedDate] = useState(selectedDateParams || defaultDate);

  const { goForward, goBackward, selected } = useCalendar(moment(selectedDate));

  const currenciesM = ['US Dollars', 'Euro', 'Pounds', 'Romanian Leu'];
  const currenciesES = ['US Dollars', 'Euro', 'Pounds', 'Romanian Leu'];
  const statsTabs = [
    { name: 'Overview stats', key: 'overview' },
    { name: 'Payment insights', key: 'insights' },
    { name: 'Exchange rates', key: 'rates' },
  ];

  const listViews = [
    { name: 'Reports', key: 'reports' },
    { name: 'Mentor history', key: 'mentor-history' },
  ];

  const [selectedCurrency, setSelectedCurrency] = useState(0);
  const [selectedESCurrency, setSelectedESCurrency] = useState(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [currentPageSize, setCurrentPageSize] = useState<number>(10);
  const [activeStatsTab, setActiveStatsTab] = useState(statsTabs[0].key);
  const [activeListView, setActiveListView] = useState(listViews[0].key);
  const isMentorHistoryView = activeListView === 'mentor-history';
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 500);
  const [selectedStatus, setSelectedStatus] = useState<number>();
  const [isTestUserFilter, setIsTestUserFilter] = useState(false);

  const [selectedItems, setSelectedItems] = useState<Report[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);

  const [renderKey, setRenderKey] = useState(0);
  const [loadingCSVData, setLoadingCSVData] = useState(false);
  const [loadingAwaitingInvoiceCSV, setLoadingAwaitingInvoiceCSV] = useState(false);
  const [loadingUnbilledCSV, setLoadingUnbilledCSV] = useState(false);
  const [loadingUnpayableExport, setLoadingUnpayableExport] = useState(false);
  const [loadingMentorDataCSV, setLoadingMentorDataCSV] = useState(false);
  const [showRemindersConfirm, setShowRemindersConfirm] = useState(false);
  const [showGenerateReport, setShowGenerateReport] = useState(false);
  const [showStuckReports, setShowStuckReports] = useState(false);

  const [rowTouched, setRowTouched] = useState(0);

  const [selectedReportId, setSelectedReportId] = useState('');

  const [showSideBySide, setShowSideBySide] = useState(false);
  const [showElectronicInvoice, setShowElectronicInvoice] = useState(false);
  const [statInfo, setStatInfo] = useState<StatInfo | null>(null);

  const [nextReportId, setNextReportId] = useState<string | null>(selectedReportId);
  const [prevReportId, setPrevReportId] = useState<string | null>(selectedReportId);

  const [sortType, setSortType] = useState('sessions');
  const [sortValue, setSortValue] = useState<'desc' | 'asc'>('desc');

  const [showAll, setShowAll] = useState(false);

  const [isLoadingDownloadBulkInvoices, setIsLoadingDownloadBulkInvoices] = useState(false);
  const [isLoadingDownloadBulkActivityReports, setIsLoadingDownloadBulkActivityReports] =
    useState(false);
  const [downloadableInvoices, setdownloadableInvoices] = useState<number>(0);
  const [downloadableActivityReports, setDownloadableActivityReports] = useState<number>(0);

  const [currentReport, setCurrentReport] = useState<Report | null>(null);

  const handleRowTouched = () => {
    setRowTouched(rowTouched + 1);
  };

  const handleSelected = (report: Report) => {
    setSelectedItems((prevSelectedItems) => {
      const isSelected = prevSelectedItems.includes(report);

      if (isSelected) {
        return prevSelectedItems.filter((selectedItem) => selectedItem !== report);
      } else {
        return [...prevSelectedItems, report];
      }
    });
  };

  const unselectAll = () => {
    setSelectedItems([]);
    setSelectAll(false);
  };

  const handleSelectAll = (reports: Report[]) => {
    if (selectAll) {
      setSelectedItems([]);
    } else {
      setSelectedItems(reports);
    }

    setSelectAll(!selectAll);
  };

  const goMLeft = () => {
    setSelectedCurrency((prevIndex) => (prevIndex === 0 ? currenciesM.length - 1 : prevIndex - 1));
  };

  const goMRight = () => {
    setSelectedCurrency((prevIndex) => (prevIndex === currenciesM.length - 1 ? 0 : prevIndex + 1));
  };

  const goESLeft = () => {
    setSelectedESCurrency((prevIndex) =>
      prevIndex === 0 ? currenciesES.length - 1 : prevIndex - 1
    );
  };

  const goESRight = () => {
    setSelectedESCurrency((prevIndex) =>
      prevIndex === currenciesES.length - 1 ? 0 : prevIndex + 1
    );
  };

  const { data: noOfActiveMentors, isLoading: isLoadingNoOfActiveMentorsByMonth } =
    useGetNoOfActiveMentorsByMonth({
      date: selectedDate,
    });
  const { data: noOfSessions, isLoading: isLoadingNoOfSessionsByMonth } = useGetNoOfSessionsByMonth(
    {
      date: selectedDate,
    }
  );
  const { data: totalPayment, isLoading: isLoadingTotalPaymentByMonth } = useGetTotalPaymentByMonth(
    {
      date: selectedDate,
    }
  );

  const { data: totalPaymentExtraService, isLoading: isLoadingTotalPaymentForExtraServiceByMonth } =
    useGetTotalPaymentForExtraServiceByMonth({
      date: selectedDate,
    });

  const { data: rateByMonth } = useGetExchangeRateByMonth({
    date: selectedDate,
  });

  const { data: reportsFinancialStats, isLoading: isLoadingFinancialStats } =
    useGetReportsFinancialStats({
      date: selectedDate,
    });

  const monthLabel = moment(selectedDate, TARGET_DATE_FORMAT).format('MMMM YYYY');
  const totalSessionsCount = reportsFinancialStats?.totalSessions ?? 0;
  const sessionsInReports = reportsFinancialStats?.sessionsInReports ?? 0;
  const sessionsInReportMonth = reportsFinancialStats?.sessionsInReportMonth ?? 0;
  const salariedSessionsInReportMonth = reportsFinancialStats?.salariedSessionsInReportMonth ?? 0;
  const monthSalaryTotalInEur = reportsFinancialStats?.monthSalaryTotalInEur ?? 0;
  const salaryTotalInEur = reportsFinancialStats?.salaryTotalInEur ?? 0;
  const totalSessionPayRateInEur = reportsFinancialStats?.totalSessionPayRateInEur ?? 0;
  const mentorSessionPayRates = reportsFinancialStats?.mentorSessionPayRates ?? [];

  const formatEuro = (value?: number | null) => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return '€0.00';
    }
    return `€${value.toFixed(2)}`;
  };

  const mentorSessionPayRateRows = mentorSessionPayRates.map((mentor) => ({
    key: mentor.mentorId,
    mentor: mentor.mentorName || mentor.mentorId,
    mentorEmail: mentor.mentorEmail || 'N/A',
    sessionsCount: mentor.sessionsCount,
    rate: mentor.isSalaried
      ? 'Salaried'
      : typeof mentor.rateAmount === 'number'
      ? `${mentor.rateAmount.toFixed(2)} ${mentor.rateCurrency}`
      : 'N/A',
    rateInEur: mentor.isSalaried ? 'Paid separately' : formatEuro(mentor.rateAmountInEur),
  }));

  const { mutate: markAsReadyForPayment } = useMarkBulkReportsReadyForPayment({
    onSuccess: () => {
      toast.success("Selected records are now marked as 'READY FOR PAYMENT'");
      unselectAll();

      handleRowTouched();
    },
  });

  const { mutate: markReportsAsPaid } = useMarkBulkReportsAsPaid({
    onSuccess: () => {
      toast.success("Selected records are now marked as 'PAID'");
      unselectAll();

      handleRowTouched();
    },
  });

  const { mutate: sendAwaitingInvoiceReminders, isLoading: isSendingReminders } =
    useSendAwaitingInvoiceReminders({
      onSuccess: (data) => {
        toast.success(`Sent ${data?.sent ?? 0} reminder email(s) to mentors.`);
      },
      onError: () => {
        toast.error('Could not send the awaiting-invoice reminders');
      },
    });

  const confirmSendRemindersHandler = () => {
    setShowRemindersConfirm(false);
    sendAwaitingInvoiceReminders({ date: selectedDate });
  };

  const { data: allMonthlyReports, isLoading } = useSearchReports({
    status: selectedStatus,
    page: currentPage,
    limit: currentPageSize,
    search: debouncedSearch,
    sortValue,
    sortType,
    date: selectedDate,
    isTestUser: isTestUserFilter,
  });

  const convertToCSV = (data: string[]) => stringify(data, { header: false });

  const { refetch: fetchAwaitingInvoiceCSV } = useGenerateAwaitingInvoiceCSVData(
    { date: selectedDate },
    { enabled: false }
  );

  const { refetch: fetchMentorDataCSV } = useGenerateMentorDataCSVData(
    {
      date: selectedDate,
      search: debouncedSearch,
      status: selectedStatus,
      sortType,
      sortValue,
      isTestUser: isTestUserFilter,
    },
    { enabled: false }
  );

  const downloadCSVFile = (csvData: string, fileName: string) => {
    const blob = new Blob([`\uFEFF${csvData}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
  };

  const downloadAwaitingInvoiceCSV = async () => {
    setLoadingAwaitingInvoiceCSV(true);
    try {
      const response = await fetchAwaitingInvoiceCSV();
      if (!response.data?.csvData) {
        toast.error('Could not generate the awaiting-invoice CSV');
        return;
      }

      downloadCSVFile(response.data.csvData, `awaiting-invoice-reports-${selectedDate}.csv`);
    } catch (error) {
      logger.error('Error downloading awaiting-invoice CSV:', error);
      toast.error('Could not generate the awaiting-invoice CSV');
    } finally {
      setLoadingAwaitingInvoiceCSV(false);
    }
  };

  const downloadUnbilledSessionsCSV = async () => {
    setLoadingUnbilledCSV(true);
    try {
      const { data } = await apiClient.get<{ csvData: string; count: number }>(
        reportApis.generateUnbilledSessionsCSV.endpoint
      );

      if (!data.count) {
        toast.info('There are no unbilled sessions from previous months.');
        return;
      }

      downloadCSVFile(data.csvData, `unbilled-sessions-${moment().format('YYYY-MM-DD')}.csv`);
      toast.success(`Exported ${data.count} unbilled sessions.`);
    } catch (error) {
      logger.error('Error downloading unbilled sessions CSV:', error);
      toast.error('Could not generate the unbilled sessions CSV');
    } finally {
      setLoadingUnbilledCSV(false);
    }
  };

  const downloadUnpayableSessionsExport = async () => {
    setLoadingUnpayableExport(true);
    try {
      const { data } = await apiClient.get<reportApis.GenerateUnpayableSessionsExport['response']>(
        reportApis.generateUnpayableSessionsExport.endpoint
      );

      if (!data.count) {
        toast.info('There are no unpayable sessions.');
        return;
      }

      const today = moment().format('YYYY-MM-DD');
      const zip = new JSZip();
      zip.file(`unpayable-sessions-${today}.csv`, `\uFEFF${data.sessionsCsv}`);
      zip.file(`unpayable-sessions-per-mentor-${today}.csv`, `\uFEFF${data.perMentorCsv}`);

      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `unpayable-sessions-${today}.zip`;
      link.click();
      URL.revokeObjectURL(url);

      toast.success(`Exported ${data.count} unpayable sessions from ${data.mentorCount} mentors.`);
    } catch (error) {
      logger.error('Error downloading unpayable sessions export:', error);
      toast.error('Could not generate the unpayable sessions export');
    } finally {
      setLoadingUnpayableExport(false);
    }
  };

  const downloadMentorDataCSV = async () => {
    setLoadingMentorDataCSV(true);
    try {
      const response = await fetchMentorDataCSV();
      if (!response.data?.csvData) {
        toast.error('Could not generate the mentor data CSV');
        return;
      }

      downloadCSVFile(response.data.csvData, `mentor-data-${selectedDate}.csv`);
    } catch (error) {
      logger.error('Error downloading mentor data CSV:', error);
      toast.error('Could not generate the mentor data CSV');
    } finally {
      setLoadingMentorDataCSV(false);
    }
  };

  const getCSVData = async () => {
    setLoadingCSVData(true);
    try {
      const response = await apiClient.get(`/reports/csv/accepted-invoices?date=${selectedDate}`);

      const currencyTypes = ['EUR', 'GBP', 'USD', 'RON'];

      const zip = new JSZip();
      currencyTypes.forEach((currencyType) => {
        const csvContent = convertToCSV(response.data[currencyType]);
        zip.file(`accepted-invoices-${currencyType}.csv`, csvContent);
      });

      zip.generateAsync({ type: 'blob' }).then((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `accepted-invoices.zip`;
        link.click();
      });
    } catch (error) {
      logger.error('Error getting CSV data:', error);
    } finally {
      setLoadingCSVData(false);
    }
  };

  const handleMarkAsReadyForPayment = (items: Report[]) => {
    if (
      items.some(
        (item: Report) =>
          item.status !== ReportStatus.INVOICE_UPLOADED &&
          item.status !== ReportStatus.INVALID_E_INVOICE &&
          item.status !== ReportStatus.INVOICE_ACCEPTED
      ) ||
      !items.length
    ) {
      toast.error(
        "In order to mark as 'READY FOR PAYMENT', all selected records must be have 'INVOICE UPLOADED', 'INVALID E INVOICE or 'INVOICE_ACCEPTED' status"
      );
      return;
    } else {
      toast.info("Marking records as 'READY FOR PAYMENT'...");
      markAsReadyForPayment({
        reportIds: items.map((item: Report) => item._id),
      });
    }
  };

  const handleMarkAsPaid = (items: Report[]) => {
    if (
      items.some((item: Report) => item.status !== ReportStatus.READY_FOR_PAYMENT) ||
      !items.length
    ) {
      toast.error(
        "In order to mark as 'PAID', all selected records must be have an 'READY FOR PAYMENT' status"
      );

      return;
    } else {
      toast.info("Marking reports and meetings as 'PAID'...");
      markReportsAsPaid({
        reportIds: items.map((item: Report) => item._id),
      });
    }
  };

  const downloadBulkInvoices = async () => {
    setIsLoadingDownloadBulkInvoices(true);
    try {
      const response = await apiClient.post(
        `/reports/bulk/download-invoices`,
        {
          invoiceIds: selectedItems
            .filter((item: any) => item.invoiceId)
            .map((item: any) => item._id),
          date: selectedDate,
          mentor: searchQuery,
          status: selectedStatus,
          isTestUser: isTestUserFilter,
        },
        { responseType: 'blob', timeout: 1000000 }
      );

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoices-${selectedDate}.zip`;
      link.click();
      setIsLoadingDownloadBulkInvoices(false);
    } catch (error) {
      setIsLoadingDownloadBulkInvoices(false);
      logger.error('Error downloading bulk invoices:', error);
    }
  };

  const downloadBulkActivityReports = async () => {
    setIsLoadingDownloadBulkActivityReports(true);
    try {
      const response = await apiClient.post(
        `/reports/bulk/download-activity-reports`,
        {
          reportIds: selectedItems.map((item: Report) => item._id),
          date: selectedDate,
          mentor: searchQuery,
          status: selectedStatus,
          isTestUser: isTestUserFilter,
        },
        { responseType: 'blob', timeout: 1000000 }
      );

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `activity_reports-${selectedDate}.zip`;
      link.click();
      setIsLoadingDownloadBulkActivityReports(false);
    } catch (error) {
      setIsLoadingDownloadBulkActivityReports(false);
      logger.error('Error downloading bulk activity reports:', error);
    }
  };

  useEffect(() => {
    if (!!allMonthlyReports && selectedReportId && (showSideBySide || showElectronicInvoice)) {
      const selectedIndex = allMonthlyReports?.reports.findIndex(
        (report: Report) => report._id === selectedReportId
      );

      for (let i = selectedIndex + 1; i < allMonthlyReports?.reports?.length; i++) {
        if (
          allMonthlyReports.reports[i]?.invoiceId &&
          allMonthlyReports.reports[i]?.status !== ReportStatus.PAID
        ) {
          setNextReportId(allMonthlyReports.reports[i]?._id);
          break;
        }
      }
      for (let i = selectedIndex - 1; i >= 0; i--) {
        if (
          allMonthlyReports.reports[i]?.invoiceId &&
          allMonthlyReports.reports[i]?.status !== ReportStatus.PAID
        ) {
          setPrevReportId(allMonthlyReports.reports[i]?._id);
          break;
        }
      }

      const currReport = allMonthlyReports?.reports?.[selectedIndex];

      setCurrentReport(currReport);

      if (!nextReportId) setNextReportId(selectedReportId);
      if (!prevReportId) setPrevReportId(selectedReportId);

      const showElectronicInvoice =
        currReport?.status === ReportStatus.INVOICE_ACCEPTED ||
        currReport?.status === ReportStatus.INVALID_E_INVOICE;

      const showSideBySide =
        currReport?.status === ReportStatus.INVOICE_UPLOADED ||
        currReport?.status === ReportStatus.READY_FOR_PAYMENT ||
        currReport?.status === ReportStatus.PAID ||
        currReport?.status === ReportStatus.REJECTED;

      setShowElectronicInvoice(showElectronicInvoice);
      setShowSideBySide(showSideBySide);
    }
  }, [
    selectedReportId,
    showSideBySide,
    showElectronicInvoice,
    nextReportId,
    prevReportId,
    allMonthlyReports?.reports,
  ]);

  useEffect(() => {
    if (selectedItems.length > 0) {
      const totalInvoices = selectedItems.filter((item: Report) => item.invoiceId).length;
      const totalActivityReports = selectedItems.filter(
        (item: Report) => item.activityReportId
      ).length;

      setdownloadableInvoices(totalInvoices);
      setDownloadableActivityReports(totalActivityReports);
    } else {
      if (allMonthlyReports) {
        setdownloadableInvoices(allMonthlyReports.totalInvoices);
        setDownloadableActivityReports(allMonthlyReports.totalDownloadableActivityReports);
      }
    }
  }, [selectedItems, allMonthlyReports]);

  const renderDropdownItem = (label: string, onClick: () => void, isDisabled = false) => (
    <div
      className={mergeClassNames(
        dropdown.dropdownItem,
        isDisabled && reportsStyles.disabledDropdownItem
      )}
      onClick={isDisabled ? undefined : onClick}
    >
      {label}
    </div>
  );

  const getBulkDownloadLabel = (label: string, count: number, isLoadingDownload: boolean) => {
    if (isLoadingDownload) return 'Loading...';
    if (count === 0) return `${label} (none)`;
    return `${label} (${count})`;
  };

  const changeMonthHandler = (callback: () => void) => {
    const newDate = moment(selected)
      .add(callback === goForward ? 1 : -1, 'month')
      .format(TARGET_DATE_FORMAT);

    callback();

    searchParams.set('selectedDate', newDate);
    history.replace(`${location.pathname}?${searchParams.toString()}`);

    setSelectedDate(newDate);
  };

  const showAllChangeHandler = () => {
    if (!allMonthlyReports) {
      return;
    }

    const show = !showAll;

    setShowAll(show);
    setCurrentPageSize(show ? allMonthlyReports?.totalActivityReports : 10);
    setCurrentPage(1);
  };

  return (
    <>
      <SideBySideModal
        visible={showSideBySide && !showElectronicInvoice}
        hide={() => setShowSideBySide(false)}
        reportId={selectedReportId}
        invoiceNo={currentReport?.invoiceNo ?? ''}
        status={currentReport?.status ?? -1}
        handleRowTouched={handleRowTouched}
        nextReport={() => {
          setSelectedReportId(nextReportId as string);
        }}
        prevReport={() => {
          setSelectedReportId(prevReportId as string);
        }}
        isFirst={selectedReportId === prevReportId}
        isLast={selectedReportId === nextReportId}
        rejectionReason={currentReport?.rejectionReason}
      />

      <ElectronicInvoiceModal
        visible={showElectronicInvoice && !showSideBySide}
        hide={() => setShowElectronicInvoice(false)}
        reportId={selectedReportId}
        invoiceNo={currentReport?.invoiceNo ?? ''}
        status={currentReport?.status ?? -1}
        handleRowTouched={handleRowTouched}
        nextReport={() => {
          setSelectedReportId(nextReportId as string);
        }}
        prevReport={() => {
          setSelectedReportId(prevReportId as string);
        }}
        isFirst={selectedReportId === prevReportId}
        isLast={selectedReportId === nextReportId}
        eInvoiceRejectionReason={currentReport?.eInvoiceRejectionReason}
      />

      <ConfirmRemindersModal
        isOpen={showRemindersConfirm}
        isLoading={isSendingReminders}
        onClose={() => setShowRemindersConfirm(false)}
        onConfirm={confirmSendRemindersHandler}
      />

      <GenerateReportModal
        isOpen={showGenerateReport}
        selectedDate={selectedDate}
        onClose={() => setShowGenerateReport(false)}
      />

      <StuckReportsModal isOpen={showStuckReports} onClose={() => setShowStuckReports(false)} />

      <Modal
        visible={!!statInfo}
        onCancel={() => setStatInfo(null)}
        footer={null}
        width={statInfo?.withMentorBreakdown ? 760 : 520}
        title={statInfo?.title}
      >
        <p style={{ margin: 0, whiteSpace: 'pre-line' }}>{statInfo?.description}</p>

        {statInfo?.withMentorBreakdown ? (
          <>
            <h4 style={{ margin: '20px 0 8px' }}>Breakdown per mentor</h4>
            <V4Table size="small">
              <V4Table.Header>
                <V4Table.Row isHeader>
                  <V4Table.HeaderCell>Mentor</V4Table.HeaderCell>
                  <V4Table.HeaderCell>Email</V4Table.HeaderCell>
                  <V4Table.HeaderCell>Sessions</V4Table.HeaderCell>
                  <V4Table.HeaderCell>Pay Rate</V4Table.HeaderCell>
                  <V4Table.HeaderCell>Pay Rate (EUR)</V4Table.HeaderCell>
                </V4Table.Row>
              </V4Table.Header>
              <V4Table.Body hasResults={!!mentorSessionPayRateRows.length}>
                {mentorSessionPayRateRows.map((row) => (
                  <V4Table.Row key={row.key}>
                    <V4Table.Cell>{row.mentor}</V4Table.Cell>
                    <V4Table.Cell>{row.mentorEmail}</V4Table.Cell>
                    <V4Table.Cell>{row.sessionsCount}</V4Table.Cell>
                    <V4Table.Cell>{row.rate}</V4Table.Cell>
                    <V4Table.Cell>{row.rateInEur}</V4Table.Cell>
                  </V4Table.Row>
                ))}
              </V4Table.Body>
            </V4Table>
            <div style={{ marginTop: '12px', color: '#9CA3AF' }}>
              Totals: {sessionsInReportMonth} sessions, {formatEuro(totalSessionPayRateInEur)}
              {salariedSessionsInReportMonth > 0
                ? ` · ${salariedSessionsInReportMonth} session(s) held by salaried mentors are priced at 0 here; their pay is the salary added to the month's total.`
                : ''}
            </div>
          </>
        ) : null}
      </Modal>

      <div className={reportsStyles.activityReportContainer}>
        <h1 className={reportsStyles.title}>Monthly Activity Report</h1>
        <div className={reportsStyles.selectorContainer}>
          <ChevronLeftIcon
            style={{ cursor: isLoading ? 'not-allowed' : 'pointer' }}
            className={`${styles.icon} ${reportsStyles.directionIcon}`}
            onClick={isLoading ? () => {} : () => changeMonthHandler(goBackward)}
          />
          <span className={reportsStyles.dateFormat}>
            {moment(selectedDate, TARGET_DATE_FORMAT)?.format('MMMM YYYY')}
          </span>
          <ChevronRightIcon
            style={{ cursor: isLoading ? 'not-allowed' : 'pointer' }}
            className={`${styles.icon} ${reportsStyles.directionIcon}`}
            onClick={isLoading ? () => {} : () => changeMonthHandler(goForward)}
          />
        </div>
      </div>

      <div style={{ padding: '24px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <div style={{ display: 'flex', borderBottom: '1px solid #4B5563' }}>
              {statsTabs.map((tab) => (
                <span
                  key={tab.key}
                  onClick={() => setActiveStatsTab(tab.key)}
                  style={{
                    cursor: 'pointer',
                    padding: '8px 16px',
                    fontWeight: 600,
                    fontSize: '14px',
                    borderBottom:
                      activeStatsTab === tab.key ? '2px solid #33A9AB' : '2px solid transparent',
                    color: activeStatsTab === tab.key ? '#33A9AB' : '#9CA3AF',
                    transition: 'color 0.2s ease',
                  }}
                >
                  {tab.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={reportsStyles.cardsContainer}>
        {activeStatsTab === 'rates' ? (
          <ExchangeRatesTab />
        ) : activeStatsTab === 'overview' ? (
          <>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '24px',
                marginBottom: '24px',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <InformationCard
                  title={
                    isLoadingNoOfActiveMentorsByMonth
                      ? 'Loading...'
                      : noOfActiveMentors?.noOfActiveMentors?.toString()
                  }
                  subtitle="Active mentors"
                  width="260px"
                  height="136px"
                />
                <h4 style={{ margin: '8px 0 0', textAlign: 'center' }}>Mentors with 1+ sessions</h4>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <InformationCard
                  title={
                    isLoadingNoOfSessionsByMonth
                      ? 'Loading...'
                      : noOfSessions?.noOfSessions?.toString()
                  }
                  subtitle="Sessions made this month"
                  width="260px"
                  height="136px"
                />
                <h4 style={{ margin: '8px 0 0', textAlign: 'center' }}>
                  Completed, feedback, review, unpayable, paid
                </h4>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <InformationCard
                  subtitle={
                    <div>
                      {rateByMonth ? rateByMonth[0]?.to?.currency : 'N/A'} ={' '}
                      <strong>
                        {rateByMonth ? rateByMonth[0]?.to?.value : 'N/A'}{' '}
                        {rateByMonth ? rateByMonth[0]?.from : 'N/A'}
                      </strong>{' '}
                      <br />
                      {rateByMonth ? rateByMonth[2]?.to?.currency : 'N/A'} ={' '}
                      <strong>
                        {rateByMonth ? rateByMonth[2]?.to?.value : 'N/A'}{' '}
                        {rateByMonth ? rateByMonth[2]?.from : 'N/A'}
                      </strong>{' '}
                      <br />
                      {rateByMonth ? rateByMonth[1]?.to?.currency : 'N/A'} ={' '}
                      <strong>
                        {rateByMonth ? rateByMonth[1]?.to?.value : 'N/A'}{' '}
                        {rateByMonth ? rateByMonth[1]?.from : 'N/A'}
                      </strong>{' '}
                      <br />
                      <br />
                      Date:{' '}
                      <strong>
                        {rateByMonth ? moment(rateByMonth[0]?.date).format('MMMM DD YYYY') : 'N/A'}
                      </strong>
                    </div>
                  }
                  width="260px"
                  height="136px"
                />
                <h4 style={{ margin: '8px 0 0', textAlign: 'center' }}>Conversion rates</h4>
              </div>
            </div>

            <div
              style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '24px' }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <InformationCard
                  title={
                    isLoadingTotalPaymentByMonth
                      ? 'Loading...'
                      : totalPayment?.totalPayment?.totalAmountInEuro !== undefined
                      ? '€' + totalPayment?.totalPayment?.totalAmountInEuro?.toFixed(2)?.toString()
                      : 'N/A'
                  }
                  subtitle="Total amount to be paid"
                  width="260px"
                  height="136px"
                />
                <h4 style={{ margin: '8px 0 0', textAlign: 'center' }}>
                  Meetings & extra services due
                </h4>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <InformationCard
                  title={
                    isLoadingTotalPaymentByMonth
                      ? 'Loading...'
                      : selectedCurrency === 0
                      ? `${
                          totalPayment?.totalPayment.paidAmountInDollars?.toFixed(2).toString() ??
                          '0'
                        } / ${
                          totalPayment?.totalPayment?.amountInDollars?.toFixed(2)?.toString() ??
                          'N/A'
                        }`
                      : selectedCurrency === 1
                      ? `${
                          totalPayment?.totalPayment.paidAmountInEuro?.toFixed(2).toString() ?? '0'
                        } / ${
                          totalPayment?.totalPayment?.amountInEuro?.toFixed(2)?.toString() ?? 'N/A'
                        }`
                      : selectedCurrency === 2
                      ? `${
                          totalPayment?.totalPayment.paidAmountInPounds?.toFixed(2).toString() ??
                          '0'
                        } / ${
                          totalPayment?.totalPayment?.amountInPounds?.toFixed(2)?.toString() ??
                          'N/A'
                        }`
                      : `${
                          totalPayment?.totalPayment.paidAmountInRON?.toFixed(2).toString() ?? '0'
                        } / ${
                          totalPayment?.totalPayment?.amountInRON?.toFixed(2)?.toString() ?? 'N/A'
                        }`
                  }
                  font="normal normal bold 26px/31px Raleway"
                  selector={
                    <CurrencySelector
                      goLeft={goMLeft}
                      goRight={goMRight}
                      selectedCurrency={selectedCurrency}
                      currencies={currenciesM}
                    />
                  }
                  width="260px"
                  height="136px"
                />
                <h4 style={{ margin: '8px 0 0', textAlign: 'center' }}>Paid / Total (meetings)</h4>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <InformationCard
                  title={
                    isLoadingTotalPaymentForExtraServiceByMonth
                      ? 'Loading...'
                      : selectedESCurrency === 0
                      ? `${
                          totalPaymentExtraService?.totalPaymentForExtraServices.paidAmountInDollars
                            ?.toFixed(2)
                            .toString() ?? '0'
                        } / ${
                          totalPaymentExtraService?.totalPaymentForExtraServices?.amountInDollars
                            ?.toFixed(2)
                            ?.toString() ?? 'N/A'
                        }`
                      : selectedESCurrency === 1
                      ? `${
                          totalPaymentExtraService?.totalPaymentForExtraServices.paidAmountInEuro
                            ?.toFixed(2)
                            .toString() ?? '0'
                        } / ${
                          totalPaymentExtraService?.totalPaymentForExtraServices?.amountInEuro
                            ?.toFixed(2)
                            ?.toString() ?? 'N/A'
                        }`
                      : selectedESCurrency === 2
                      ? `${
                          totalPaymentExtraService?.totalPaymentForExtraServices.paidAmountInPounds
                            ?.toFixed(2)
                            .toString() ?? '0'
                        } / ${
                          totalPaymentExtraService?.totalPaymentForExtraServices?.amountInPounds
                            ?.toFixed(2)
                            ?.toString() ?? 'N/A'
                        }`
                      : `${
                          totalPaymentExtraService?.totalPaymentForExtraServices.paidAmountInRON
                            ?.toFixed(2)
                            .toString() ?? '0'
                        } / ${
                          totalPaymentExtraService?.totalPaymentForExtraServices?.amountInRON
                            ?.toFixed(2)
                            ?.toString() ?? 'N/A'
                        }`
                  }
                  font="normal normal bold 26px/31px Raleway"
                  selector={
                    <CurrencySelector
                      goLeft={goESLeft}
                      goRight={goESRight}
                      selectedCurrency={selectedESCurrency}
                      currencies={currenciesES}
                    />
                  }
                  width="260px"
                  height="136px"
                />
                <h4 style={{ margin: '8px 0 0', textAlign: 'center' }}>
                  Paid / Total (extra services)
                </h4>
              </div>
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: '32px' }}>
              <h3 style={{ textAlign: 'center', margin: '0 0 4px' }}>
                Sessions held in {monthLabel}
              </h3>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: '24px',
                }}
              >
                <StatCard
                  onInfoClick={setStatInfo}
                  value={isLoadingFinancialStats ? 'Loading...' : sessionsInReportMonth.toString()}
                  cardSubtitle="Sessions held"
                  title={`Sessions held in ${monthLabel}`}
                  description={
                    salariedSessionsInReportMonth > 0
                      ? `Every mentoring session held in ${monthLabel}, by everyone, including the ${salariedSessionsInReportMonth} held by salaried mentors. Onboarding and education strategy meetings are not counted.`
                      : `All sessions held in ${monthLabel}. Sessions marked unpayable are not counted.`
                  }
                />

                <StatCard
                  onInfoClick={setStatInfo}
                  value={
                    isLoadingFinancialStats ? 'Loading...' : formatEuro(totalSessionPayRateInEur)
                  }
                  cardSubtitle="Cost of these sessions (EUR)"
                  title={`Total to pay for ${monthLabel}`}
                  description={`Each session above priced at its mentor's current pay rate, VAT added where the mentor is a VAT payer, converted to EUR at that month's exchange rate. Salaried mentors add 0 per session; instead their pay enters here as the ${formatEuro(
                    monthSalaryTotalInEur
                  )} of salaries booked for ${monthLabel}. Any other extra service is not part of this figure. This is what ${monthLabel} alone costs, not everything that is currently due.`}
                />

                <StatCard
                  onInfoClick={setStatInfo}
                  value={
                    isLoadingFinancialStats
                      ? 'Loading...'
                      : sessionsInReportMonth > 0
                      ? formatEuro(reportsFinancialStats?.averageSessionPayRate)
                      : 'N/A'
                  }
                  cardSubtitle="Average per session (EUR)"
                  title={`Average price of a ${monthLabel} session`}
                  description={`The total on the left, salaries included, divided by all ${sessionsInReportMonth} session(s) held in ${monthLabel}. Every mentoring session counts, whoever held it, so this is the blended cost of a session. Earlier months are not part of this figure.`}
                  withMentorBreakdown
                />
              </div>
            </div>

            <div>
              <h3 style={{ textAlign: 'center', margin: '0 0 4px' }}>
                Waiting to be paid ({monthLabel} + earlier unpaid months)
              </h3>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: '24px',
                }}
              >
                <StatCard
                  onInfoClick={setStatInfo}
                  value={isLoadingFinancialStats ? 'Loading...' : sessionsInReports.toString()}
                  cardSubtitle="Sessions listed in reports"
                  title="Sessions put in reports"
                  description="Every session written into these reports, whatever its status: to be paid, still waiting for mentor feedback, under review, or marked unpayable. Use it to see the full workload the reports cover; only part of it turns into money."
                />

                <StatCard
                  onInfoClick={setStatInfo}
                  value={isLoadingFinancialStats ? 'Loading...' : totalSessionsCount.toString()}
                  cardSubtitle="Sessions to be paid"
                  title="Sessions we have to pay"
                  description={`The subset of the sessions above that is actually payable: completed and not yet paid, from ${monthLabel} plus anything carried over from earlier months that was never invoiced or paid.`}
                />

                <StatCard
                  onInfoClick={setStatInfo}
                  value={
                    isLoadingFinancialStats
                      ? 'Loading...'
                      : formatEuro(reportsFinancialStats?.totalAmountInEur)
                  }
                  cardSubtitle="Amount to be paid (EUR)"
                  title="Total to pay"
                  description={`The sessions on the left priced at the pay rate stored on each report, VAT included where applicable, converted to EUR, plus the ${formatEuro(
                    salaryTotalInEur
                  )} of salaries bundled into those reports. Salaried mentors add 0 per session; their pay is the salary. Any other extra service is not part of this figure.`}
                />

                <StatCard
                  onInfoClick={setStatInfo}
                  value={
                    isLoadingFinancialStats
                      ? 'Loading...'
                      : totalSessionsCount > 0
                      ? formatEuro(reportsFinancialStats?.averagePayPerSession)
                      : 'N/A'
                  }
                  cardSubtitle="Average per session (EUR)"
                  title={`Average price of a session we owe (${monthLabel} + earlier)`}
                  description={`The total on the left, salaries included, divided by all ${totalSessionsCount} session(s) we still have to pay. Every mentoring session counts, whoever held it.`}
                />
              </div>
            </div>
          </>
        )}
      </div>

      <div style={{ marginTop: '40px' }}></div>

      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', borderBottom: '1px solid #4B5563' }}>
          {listViews.map((view) => (
            <span
              key={view.key}
              onClick={() => setActiveListView(view.key)}
              style={{
                cursor: 'pointer',
                padding: '8px 16px',
                fontWeight: 600,
                fontSize: '14px',
                borderBottom:
                  activeListView === view.key ? '2px solid #33A9AB' : '2px solid transparent',
                color: activeListView === view.key ? '#33A9AB' : '#9CA3AF',
                transition: 'color 0.2s ease',
              }}
            >
              {view.name}
            </span>
          ))}
        </div>
      </div>

      {isMentorHistoryView ? (
        <div className={reportsStyles.reportsContainer}>
          <MentorActivityTab />
        </div>
      ) : (
        <div className={reportsStyles.reportsContainer}>
          <div className={reportsStyles.filtersGroup}>
            <h2 style={{ margin: 0 }}>Reports</h2>
            <Input
              placeholder="Search by mentor"
              size="middle"
              value={searchQuery}
              onChange={(e) => {
                unselectAll();
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              suffix={suffix}
              className={reportsStyles.search}
            />
            <Select
              style={{ width: '200px' }}
              showSearch
              placeholder="Invoice Status"
              optionFilterProp="children"
              onChange={(value: number) => {
                unselectAll();
                setSelectedStatus(value);
                setCurrentPage(1);
              }}
            >
              <Select.Option value={undefined}>ALL</Select.Option>
              {Object.keys(ReportStatus)
                .filter((key) => isNaN(Number(key)))
                .map((key) => (
                  <Select.Option key={key} value={ReportStatus[key as keyof ReportStatus as any]}>
                    {key.replaceAll('_', ' ')}
                  </Select.Option>
                ))}
            </Select>

            <div className={reportsStyles.filtersGroupItem}>
              <span className="text-white">Test User</span>
              <Checkbox
                onChange={(e) => {
                  unselectAll();
                  setIsTestUserFilter(e.target.checked);
                  setCurrentPage(1);
                }}
                checked={isTestUserFilter}
              />
            </div>

            <div className={reportsStyles.filtersGroupItem}>
              <span className="text-white">Sort by:</span>
              <Select
                style={{ width: 'auto' }}
                showSearch
                placeholder="Sort by"
                optionFilterProp="children"
                onChange={(value: string) => {
                  unselectAll();
                  setSortType(value);
                  setCurrentPage(1);
                }}
                defaultValue={sortType}
              >
                <Select.Option value={'name'}>NAME</Select.Option>
                <Select.Option value={'sessions'}>SESSIONS</Select.Option>
              </Select>
            </div>

            <div className={reportsStyles.filtersGroupItem}>
              <span className="text-white">Order:</span>
              <Select
                style={{ width: 'auto' }}
                showSearch
                placeholder="Sort order"
                optionFilterProp="children"
                onChange={(value) => {
                  unselectAll();
                  setSortValue(value);
                }}
                defaultValue={sortValue}
              >
                <Select.Option value={'asc'}>ASCENDING</Select.Option>
                <Select.Option value={'desc'}>DESCENDING</Select.Option>
              </Select>
            </div>
          </div>

          <div className={reportsStyles.actionsGroup}>
            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              overlay={
                <div className={dropdown.dropdown}>
                  <span className={reportsStyles.dropdownHint}>Current view</span>
                  {renderDropdownItem(
                    loadingMentorDataCSV ? 'Loading...' : 'Mentor data (CSV)',
                    downloadMentorDataCSV,
                    loadingMentorDataCSV
                  )}
                  {renderDropdownItem(
                    getBulkDownloadLabel(
                      'Invoices',
                      downloadableInvoices,
                      isLoadingDownloadBulkInvoices
                    ),
                    downloadBulkInvoices,
                    isLoadingDownloadBulkInvoices || downloadableInvoices === 0
                  )}
                  {renderDropdownItem(
                    getBulkDownloadLabel(
                      'Activity reports',
                      downloadableActivityReports,
                      isLoadingDownloadBulkActivityReports
                    ),
                    downloadBulkActivityReports,
                    isLoadingDownloadBulkActivityReports || downloadableActivityReports === 0
                  )}

                  <span className={reportsStyles.dropdownHint}>Whole month</span>
                  {renderDropdownItem(
                    loadingAwaitingInvoiceCSV ? 'Loading...' : 'Awaiting invoice (CSV)',
                    downloadAwaitingInvoiceCSV,
                    loadingAwaitingInvoiceCSV
                  )}
                  {renderDropdownItem(
                    loadingCSVData ? 'Loading...' : 'Ready for payment (ZIP)',
                    getCSVData,
                    loadingCSVData
                  )}

                  <span className={reportsStyles.dropdownHint}>All months</span>
                  {renderDropdownItem(
                    loadingUnbilledCSV ? 'Loading...' : 'Unbilled sessions (CSV)',
                    downloadUnbilledSessionsCSV,
                    loadingUnbilledCSV
                  )}
                  {renderDropdownItem(
                    loadingUnpayableExport ? 'Loading...' : 'Unpayable sessions (ZIP)',
                    downloadUnpayableSessionsExport,
                    loadingUnpayableExport
                  )}
                </div>
              }
            >
              <Button type="primary" className={reportsStyles.actionButton}>
                <DownloadIcon className={reportsStyles.actionButtonIcon} />
                Download
                <ChevronDownIcon className={reportsStyles.actionButtonIcon} />
              </Button>
            </Dropdown>

            <Dropdown
              trigger={['click']}
              placement="bottomRight"
              disabled={!selectedItems.length}
              overlay={
                <div className={dropdown.dropdown}>
                  <span className={reportsStyles.dropdownHint}>
                    {`${selectedItems.length} selected report(s)`}
                  </span>
                  {renderDropdownItem('Mark as ready for payment', () =>
                    handleMarkAsReadyForPayment(selectedItems)
                  )}
                  {renderDropdownItem('Mark as paid', () => handleMarkAsPaid(selectedItems))}
                </div>
              }
            >
              <Button
                type="primary"
                className={reportsStyles.actionButton}
                disabled={!selectedItems.length}
              >
                <DotsVerticalIcon className={reportsStyles.actionButtonIcon} />
                {selectedItems.length ? `Bulk actions (${selectedItems.length})` : 'Bulk actions'}
                <ChevronDownIcon className={reportsStyles.actionButtonIcon} />
              </Button>
            </Dropdown>

            <Button
              type="primary"
              className={reportsStyles.actionButton}
              onClick={() => setShowGenerateReport(true)}
            >
              <PlusIcon className={reportsStyles.actionButtonIcon} />
              Generate report
            </Button>

            <Button
              type="primary"
              className={reportsStyles.actionButton}
              onClick={() => setShowStuckReports(true)}
            >
              <ExclamationIcon className={reportsStyles.actionButtonIcon} />
              Stuck reports
            </Button>

            <Button
              type="primary"
              className={reportsStyles.actionButton}
              disabled={isSendingReminders}
              onClick={() => setShowRemindersConfirm(true)}
            >
              <MailIcon className={reportsStyles.actionButtonIcon} />
              {isSendingReminders ? 'Sending...' : 'Send reminders'}
            </Button>
          </div>
        </div>
      )}

      {!isMentorHistoryView && allMonthlyReports && !isLoading && (
        <ActivityReportTable
          handleRowTouched={handleRowTouched}
          selectedDate={selected}
          key={renderKey}
          handleSelected={handleSelected}
          handleSelectAll={handleSelectAll}
          selectedItems={selectedItems}
          selectAll={selectAll}
          isLoading={isLoading}
          reports={allMonthlyReports?.reports}
          setSelectedReportId={setSelectedReportId}
          setShowSideBySideModal={setShowSideBySide}
          setShowElectronicInvoiceModal={setShowElectronicInvoice}
        />
      )}

      <div
        style={{
          display: isMentorHistoryView ? 'none' : 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
        }}
        className={reportsStyles.paginationContainer}
      >
        {!showAll && (
          <Pagination
            className="mx-6"
            total={allMonthlyReports?.totalActivityReports}
            currentPage={currentPage}
            pageSize={currentPageSize}
            onPageSizeChange={setCurrentPageSize}
            onCurrentPageChange={setCurrentPage}
          />
        )}
        {!!allMonthlyReports?.totalActivityReports && (
          <div
            className="min-w-max h-20"
            style={{
              marginLeft: '8px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <span
              style={{
                display: 'inline-block',
                height: '32px',
                marginRight: '8px',
                lineHeight: '30px',
                verticalAlign: 'middle',
                fontSize: '14px',
              }}
            >
              Show All:{' '}
            </span>
            <Checkbox checked={showAll} onChange={showAllChangeHandler} />
          </div>
        )}
      </div>
    </>
  );
};

export default ActivityReport;
